// ============================================
// FORM DATA STORE (Zustand)
// ============================================

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { FormData, PersonalDetails, CurrentSetup, UsageDetails, TechnicalPreferences, FuturePlans } from '../types';

// Storage key for form data
const STORAGE_KEY = 'device-recommendation-form';

interface FormState {
  currentStep: number;
  formData: FormData;
  leadId: string | null;
  isSubmitting: boolean;
  isCompleted: boolean; // Track if form was successfully completed
  
  // Navigation
  nextStep: () => void;
  prevStep: () => void;
  goToStep: (step: number) => void;
  
  // Data updates
  updatePersonalDetails: (data: Partial<PersonalDetails>) => void;
  updateCurrentSetup: (data: Partial<CurrentSetup>) => void;
  updateUsageDetails: (data: Partial<UsageDetails>) => void;
  updateTechnicalPreferences: (data: Partial<TechnicalPreferences>) => void;
  updateFuturePlans: (data: Partial<FuturePlans>) => void;
  
  // Lead management
  setLeadId: (id: string) => void;
  clearLeadId: () => void;
  setSubmitting: (submitting: boolean) => void;
  
  // Completion
  markCompleted: () => void;
  
  // Validation
  isStepValid: (step: number) => boolean;
  
  // Check if there's saved progress
  hasSavedProgress: () => boolean;
  
  // Reset
  resetForm: () => void;
  clearAllData: () => void;
  startNewForm: () => void;
}

const initialFormData: FormData = {
  personalDetails: {
    name: '',
    email: '',
    phone: '',
    location: '',
    age: '',
    occupation: '',
    whatsappSameAsPhone: true,
    whatsappNumber: '',
  },
  currentSetup: {
    hasCurrentDevice: false,
    currentDeviceType: 'none',
    currentDeviceBrand: '',
    currentDeviceAge: '',
    currentDeviceCondition: 'no_device',
    painPoints: [],
  },
  usageDetails: {
    deviceIntent: 'laptop',
    persona: 'professional',
    primaryUsage: [],
    secondaryUsage: [],
    dailyHours: '2_to_4',
    workFromHome: false,
    needsPortability: 'somewhat_important',
    osPreference: 'no_preference',
    specificSoftware: '',
  },
  technicalPreferences: {
    screenSizePreference: 'no_preference',
    storageNeeds: 'moderate_512gb',
    brandPreference: [],
    brandAvoid: [],
    priorityFeatures: [],
  },
  futurePlans: {
    plannedActivities: [],
    budgetRange: '50k_to_80k',
    budgetFlexibility: 'flexible_10_percent',
    urgency: 'within_month',
    expectedUsageYears: '3_4_years',
    additionalNotes: '',
  },
};

export const useFormStore = create<FormState>()(
  persist(
    (set, get) => ({
      currentStep: 1,
      formData: initialFormData,
      leadId: null,
      isSubmitting: false,
      isCompleted: false,

      nextStep: () => {
        const { currentStep, isStepValid } = get();
        if (isStepValid(currentStep) && currentStep < 6) {
          set({ currentStep: currentStep + 1 });
        }
      },

      prevStep: () => {
        const { currentStep } = get();
        if (currentStep > 1) {
          set({ currentStep: currentStep - 1 });
        }
      },

      goToStep: (step: number) => {
        if (step >= 1 && step <= 6) {
          set({ currentStep: step });
        }
      },

      updatePersonalDetails: (data) => {
        set((state) => ({
          formData: {
            ...state.formData,
            personalDetails: {
              ...state.formData.personalDetails,
              ...data,
            },
          },
        }));
      },

      updateCurrentSetup: (data) => {
        set((state) => ({
          formData: {
            ...state.formData,
            currentSetup: {
              ...state.formData.currentSetup,
              ...data,
            },
          },
        }));
      },

      updateUsageDetails: (data) => {
        set((state) => ({
          formData: {
            ...state.formData,
            usageDetails: {
              ...state.formData.usageDetails,
              ...data,
            },
          },
        }));
      },

      updateTechnicalPreferences: (data) => {
        set((state) => ({
          formData: {
            ...state.formData,
            technicalPreferences: {
              ...state.formData.technicalPreferences,
              ...data,
            },
          },
        }));
      },

      updateFuturePlans: (data) => {
        set((state) => ({
          formData: {
            ...state.formData,
            futurePlans: {
              ...state.formData.futurePlans,
              ...data,
            },
          },
        }));
      },

      setLeadId: (id) => set({ leadId: id }),
      
      clearLeadId: () => set({ leadId: null }),
      
      setSubmitting: (submitting) => set({ isSubmitting: submitting }),

      isStepValid: (step: number) => {
        const { formData } = get();
        
        switch (step) {
          case 1: {
            // Personal Details
            const { name, email, phone, location, whatsappSameAsPhone, whatsappNumber } = formData.personalDetails;
            const baseValid = (
              name.trim().length >= 2 &&
              /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) &&
              /^[6-9]\d{9}$/.test(phone.replace(/\s/g, '')) &&
              location.trim().length >= 2
            );
            // If WhatsApp is different, validate that number too
            if (!whatsappSameAsPhone && whatsappNumber) {
              return baseValid && /^[6-9]\d{9}$/.test(whatsappNumber.replace(/\s/g, ''));
            }
            return baseValid;
          }
          case 2: {
            // Current Setup - always valid (optional info)
            return true;
          }
          case 3: {
            // Usage Details
            const { deviceIntent, persona, primaryUsage, dailyHours } = formData.usageDetails;
            return (
              deviceIntent !== undefined &&
              persona !== undefined &&
              primaryUsage.length > 0 &&
              dailyHours !== undefined
            );
          }
          case 4: {
            // Technical Preferences - always valid (optional)
            return true;
          }
          case 5: {
            // Future Plans
            const { budgetRange, urgency } = formData.futurePlans;
            return budgetRange !== undefined && urgency !== undefined;
          }
          case 6:
            return true; // Review step is always valid if we reach it
          default:
            return false;
        }
      },

      resetForm: () => {
        set({
          currentStep: 1,
          formData: initialFormData,
          leadId: null,
          isSubmitting: false,
          isCompleted: false,
        });
      },

      clearAllData: () => {
        // Clear persisted storage completely
        sessionStorage.removeItem(STORAGE_KEY);
        set({
          currentStep: 1,
          formData: initialFormData,
          leadId: null,
          isSubmitting: false,
          isCompleted: false,
        });
      },

      startNewForm: () => {
        // Clear storage and start fresh - for creating a new request
        sessionStorage.removeItem(STORAGE_KEY);
        set({
          currentStep: 1,
          formData: initialFormData,
          leadId: null,
          isSubmitting: false,
          isCompleted: false,
        });
      },

      markCompleted: () => {
        set({ isCompleted: true });
        // Clear storage after successful completion so next visit starts fresh
        sessionStorage.removeItem(STORAGE_KEY);
      },

      hasSavedProgress: () => {
        const { formData, currentStep, isCompleted } = get();
        // If form was completed, no saved progress to resume
        if (isCompleted) return false;
        // Check if user has made any progress (not on step 1 or has entered data)
        if (currentStep > 1) return true;
        // Check if personal details have been filled
        const { name, email, phone } = formData.personalDetails;
        return name.trim().length > 0 || email.trim().length > 0 || phone.trim().length > 0;
      },
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => sessionStorage), // Use sessionStorage instead of localStorage
      partialize: (state) => ({
        formData: state.formData,
        currentStep: state.currentStep,
        leadId: state.leadId,
        isCompleted: state.isCompleted,
        // NOTE: isSubmitting is NOT persisted - it should always start as false
      }),
      // On rehydration, ensure isSubmitting is always false
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.isSubmitting = false;
          // If form was already completed, reset it for a new session
          if (state.isCompleted) {
            state.currentStep = 1;
            state.formData = initialFormData;
            state.leadId = null;
            state.isCompleted = false;
          }
        }
      },
    }
  )
);
